import { useState, useEffect } from 'react';
import { logger } from '@ninja/utilities';
import { useRouter } from './useRouter2.js';

const useLiveChatWidget = ({ userInfo, appConfig, productName, customWidgetColor }) => {
    const [isOmniChatReady, setIsOmniChatReady] = useState(false);
    const { locale, pathname } = useRouter();
    const { isUserLoggedIn, user } = userInfo;
    const { orgId, appId, orgUrl, appId_AR } = appConfig;
    const chatExists = () => {
        var _a, _b;
        return typeof window !== 'undefined' &&
            ((_b = (_a = window === null || window === void 0 ? void 0 : window.Microsoft) === null || _a === void 0 ? void 0 : _a.Omnichannel) === null || _b === void 0 ? void 0 : _b.LiveChatWidget) &&
            !!document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window');
    };
    const updateContext = () => {
        var _a, _b, _c, _d;
        if (isOmniChatReady) {
            (_d = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.Microsoft) === null || _a === void 0 ? void 0 : _a.Omnichannel) === null || _b === void 0 ? void 0 : _b.LiveChatWidget) === null || _c === void 0 ? void 0 : _c.SDK) === null || _d === void 0 ? void 0 : _d.setContextProvider(() => {
                var _a, _b, _c, _d;
                return {
                    isLoggedIn: { value: isUserLoggedIn === null || isUserLoggedIn === void 0 ? void 0 : isUserLoggedIn.toString(), isDisplayable: true },
                    email: {
                        value: (_b = (_a = user === null || user === void 0 ? void 0 : user.email) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '',
                        isDisplayable: !!(user === null || user === void 0 ? void 0 : user.email),
                    },
                    mobileNumber: {
                        value: `${(_d = (_c = user === null || user === void 0 ? void 0 : user.mobileNumber) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : ''}`,
                        isDisplayable: !!(user === null || user === void 0 ? void 0 : user.mobileNumber),
                    },
                    currentWebUrl: { value: pathname === null || pathname === void 0 ? void 0 : pathname.toString(), isDisplayable: true },
                    locale: { value: locale !== null && locale !== void 0 ? locale : 'en', isDisplayable: true },
                    project: {
                        value: productName,
                        isDisplayable: true,
                    },
                };
            });
        }
    };
    useEffect(() => {
        updateContext();
    }, [isUserLoggedIn, isOmniChatReady, locale, pathname, productName, user === null || user === void 0 ? void 0 : user.email, user === null || user === void 0 ? void 0 : user.mobileNumber]);
    useEffect(() => {
        if (!isOmniChatReady) {
            initializeChatWidget();
        }
        const chatReady = () => {
            setIsOmniChatReady(true);
        };
        window.addEventListener('lcw:ready', chatReady);
        return () => {
            if (isOmniChatReady) {
                clearChatWidget();
            }
        };
    }, []);
    useEffect(() => {
        changeOmniChatLang();
    }, [locale]);
    const initializeChatWidget = () => {
        let script = document.querySelector('#Microsoft_Omnichannel_LCWidget');
        if (!script) {
            script = document.createElement('script');
            script.src = `https://oc-cdn-public-eur.azureedge.net/livechatwidget/scripts/LiveChatBootstrapper.js?appId=${new Date().getTime()}`;
            script.async = true;
            script.id = 'Microsoft_Omnichannel_LCWidget';
            script.type = 'text/javascript';
            script.defer = true;
            script.setAttribute('data-lcw-version', 'prod');
            script.setAttribute('data-org-id', orgId);
            script.setAttribute('data-app-id', locale === 'ar' ? appId_AR : appId);
            script.setAttribute('data-org-url', orgUrl);
            script.setAttribute('data-color-override', customWidgetColor !== null && customWidgetColor !== void 0 ? customWidgetColor : '#1D68FF');
            script.setAttribute('data-hide-chat-button', 'true');
            script.setAttribute('data-disable-telemetry', 'true');
            script.setAttribute('v2', '');
            document.body.appendChild(script);
        }
    };
    const logoutChatClient = () => {
        var _a, _b, _c, _d;
        if (chatExists() && isOmniChatReady) {
            (_d = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.Microsoft) === null || _a === void 0 ? void 0 : _a.Omnichannel) === null || _b === void 0 ? void 0 : _b.LiveChatWidget) === null || _c === void 0 ? void 0 : _c.SDK) === null || _d === void 0 ? void 0 : _d.closeChat();
        }
        setIsOmniChatReady(false);
    };
    const changeOmniChatLang = () => {
        clearChatWidget();
        initializeChatWidget();
        if (chatExists()) {
            const element = document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window');
            element === null || element === void 0 ? void 0 : element.remove();
        }
    };
    const clearChatWidget = () => {
        var _a;
        logoutChatClient();
        // remove the script if it is already there
        if (document.querySelector('#Microsoft_Omnichannel_LCWidget')) {
            window.document.body.removeChild(window.document.querySelector('#Microsoft_Omnichannel_LCWidget'));
        }
        if (document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window')) {
            (_a = document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window')) === null || _a === void 0 ? void 0 : _a.remove();
        }
        window.removeEventListener('lcw:ready', () => {
            logger.info('[Omni Chat][useEffect] Remove lcw:ready event listener');
        });
    };
    const startChat = () => {
        var _a, _b, _c, _d;
        if (chatExists()) {
            logger.info('[Omni Chat] Start Chat');
            (_d = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.Microsoft) === null || _a === void 0 ? void 0 : _a.Omnichannel) === null || _b === void 0 ? void 0 : _b.LiveChatWidget) === null || _c === void 0 ? void 0 : _c.SDK) === null || _d === void 0 ? void 0 : _d.startChat({
                inNewWindow: false,
            });
        }
    };
    return {
        startChat,
        isChatReady: isOmniChatReady,
        logoutChatClient,
    };
};

export { useLiveChatWidget as default };
